
import { DISCOUNT_CATEGORY } from '~~/types/discounts';
import type { MappedDiscount } from '~~/types/discounts';
import type { LoyaltyLevel } from '~~/types/loyaltyLevel/loyaltyLevel';

export const useInactiveLoyaltyDiscount = () => {

  const { isLoggedIn } = useUserExtended();
  const { hasPersonalizedDiscount } = usePersonalizedDiscount();
  const { isLoyaltyDiscountEnabled } = useStoreVsfExtended();
  const { loyaltyLevels, highestLoyaltyDiscountPercentage } = useCTLoyaltyLevels();

  const nextLoyaltyLevel = computed<LoyaltyLevel | null>(() => {

    if (!loyaltyLevels.value?.length) {

      return null;
    
    }

    // Find the lowest level that has a discount > 0 (typically Level 2)
    return loyaltyLevels.value
      .filter(level => level.discountPercentage > 0)
      .sort((a, b) => a.levelNumber - b.levelNumber)[0] || null;
  
  });

  const inactiveLoyaltyDiscount = computed<MappedDiscount | null>(() => {

    if (!isLoyaltyDiscountEnabled.value) {

      return null;
    
    }

    const hasNoDiscountButCanGetOne = !hasPersonalizedDiscount.value && !!nextLoyaltyLevel.value;

    if (!highestLoyaltyDiscountPercentage.value || !hasNoDiscountButCanGetOne) {

      return null;
    
    }
    // Common properties for both discount types
    const commonProps = {
      name: 'expondo Partners',
      type: 'relative' as const,
      target: 'lineItems' as const,
      discountedAmount: 0,
      requiresDiscountCode: false,
      products: [],
      category: DISCOUNT_CATEGORY.LOYALTY,
      minimalQuantity: undefined,
    };

    if (!isLoggedIn.value) {

      // Not logged in user - show general loyalty potential
      return {
        ...commonProps,
        id: 'inactive-loyalty-discount-not-logged-in',
        value: highestLoyaltyDiscountPercentage.value,
        loyaltyLevel: undefined,
      };
    
    } else if (hasNoDiscountButCanGetOne) {

      // Logged in user without discount - show specific next level
      return {
        ...commonProps,
        id: 'inactive-loyalty-discount-logged-in',
        value: nextLoyaltyLevel.value.discountPercentage,
        loyaltyLevel: {
          levelNumber: nextLoyaltyLevel.value.levelNumber,
          spendingRange: nextLoyaltyLevel.value.spendingRange,
        },
      };
    
    }

    return null;
  
  });

  return {
    inactiveLoyaltyDiscount,
  };

};
